import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faLanguage,
  faLocationCrosshairs,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import {
  DataBindingDirective,
  GridDataResult,
  PageChangeEvent,
  PagerPosition,
  PagerType,
  RowArgs,
  SelectionEvent,
} from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { process } from '@progress/kendo-data-query';
import { SVGIcon, filePdfIcon, searchIcon } from '@progress/kendo-svg-icons';
import { some } from 'lodash';
import moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AdminService } from 'src/app/modules/administration/services/admin.service';
import { LoadingService } from 'src/app/services/loading.service';
import {
  dialogAnimationDuration,
  dialogTimer,
} from 'src/app/shared/constants/dialog-specs';

import { AgencyRequest, workListRequest } from 'src/app/modules/administration/interfaces/add-to-worklist';
import { VwCompleteWorklist } from 'src/app/modules/administration/interfaces/complete-worklist';
import { VwMdeWaitlist } from 'src/app/modules/administration/interfaces/mde-waitlist';
import { Permissions } from 'src/app/shared/enums/permissions';
import { Permission } from 'src/app/shared/models/permission';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import { AgencyList } from '../../interface/agency-list';
import { PageSize } from '../enums/page-sizes';
@Component({
  selector: 'app-wait-list',
  templateUrl: './wait-list.component.html',
  styleUrls: ['./wait-list.component.scss'],
  standalone: false,
})
export class WaitListComponent implements OnInit {
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
  
  // MDE Waitlist Grid
  public gridView: GridDataResult;
  public gridData: VwMdeWaitlist[] = [];
  public mySelection: string[] = [];
  public mdeSkip: number = 0;
  public mdePageSize: number = 20;

  // Global Worklist Grid
  public globalGridView: GridDataResult;
  public globalGridData: VwCompleteWorklist[] = [];
  public globalMySelection: string[] = [];
  public globalSkip: number = 0;
  public globalPageSize: number = 20;

  showNewReferralModal$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  showAssignStudentToAgencies$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public searchIcon: SVGIcon = searchIcon;
  public pdfSVG: SVGIcon = filePdfIcon;
  selectedGlobalWaitlist: string[];
  public types: PagerType = 'numeric';
  readonly emptySelectionCount: number = 0;
  readonly buttonCount: number = 5;
  readonly phoneLength: number = 10;
  readonly areaCode: number = 3;
  readonly phoneNumberEnd: number = 6;
  public info: boolean = true;
  public pageSizes: number[] = [
    PageSize.Minimum,
    PageSize.Few,
    PageSize.Some,
    PageSize.Many,
  ];
  public previousNext: boolean = true;
  public position: PagerPosition = 'bottom';

  selectedWaitlist: AgencyRequest[] = [];
  agencyId: string;
  type: string;
  loadSpinner: boolean;
  getAgencyDetails: AgencyList;
  mdeWaitListForm: FormGroup = new FormGroup({
    mdeWaitListSearch: new FormControl(''),
  });
  globalForm: FormGroup = new FormGroup({
    globalSearch: new FormControl(''),
  });
  user: User;
  locationCode: string;
  buildingCode: string;
  selectedAgency:VwCompleteWorklist[] = [] ;

  constructor(
    library: FaIconLibrary,
    private adminService: AdminService,
    private router: ActivatedRoute,
    private authStore: AuthStore,
    private notificationService: NotificationService,
    private loadingService: LoadingService
  ) {
    library.addIcons(faLocationCrosshairs, faLanguage, faSearch);
    this.agencyId = this.router.snapshot.paramMap.get('id');
    this.type = this.router.snapshot.paramMap.get('type');
    this.authStore.UserInfo$.subscribe((userInfo: User) => {
      this.user = userInfo;
    });
  }

  public ngOnInit(): void {
    if (this.type === 'MDE') {
      // check user type
      forkJoin([
        this.getMdeWaitlist(),
        this.adminService.getMdeAgencyByAgencySk(this.agencyId),
      ]).subscribe(
        ([mdeWaitList, mdeAgencyByAgencySk]: [
          GridDataResult,
          AgencyList
        ]) => {
          this.getAgencyDetails = mdeAgencyByAgencySk;
        }
      );
    } else {
      this.gobalWorklist();
    }
    this.formChanges();
  }

  formChanges(): void {
    this.mdeWaitListForm.get('mdeWaitListSearch')?.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => {
        this.mdeSkip = 0;
      }),
      switchMap(() => this.getMdeWaitlist())
    ).subscribe();

  this.globalForm.get('globalSearch')?.valueChanges.pipe(
    debounceTime(400),
    distinctUntilChanged(),
    tap(() => {
      this.globalSkip = 0;
    })).subscribe(() => {
    this.gobalWorklist();
  });
  }

  getMdeWaitlist(): Observable<GridDataResult> {
  const payload: workListRequest = {
    pageNumber: Math.floor(this.mdeSkip / this.mdePageSize) + 1,
    pageSize: this.mdePageSize,
    studentName: this.mdeWaitListForm.get('mdeWaitListSearch')?.value || '',
  };
  this.loadingService.show();
  return this.adminService.getMdeWaitlist(this.agencyId, payload).pipe(
    map((response: VwMdeWaitlist[]) => {
      this.saveMdeWaitlist(response);
      return {
        data: response || [],
        total: response[0]?.totalCount || 0,
      };
    }),
    finalize(() => this.loadingService.hide())
  );
}

  saveMdeWaitlist(response: VwMdeWaitlist[]): void {
    this.gridData = response || [];
    this.gridView =  {
      data: this.gridData,
      total: response[0]?.totalCount || 0,
    };
  }

  gobalWorklist(): void {
  const payload: workListRequest = {
    studentName: this.globalForm.get('globalSearch')?.value || '',
    pageNumber: Math.floor(this.globalSkip / this.globalPageSize) + 1,
    pageSize: this.globalPageSize,
  };
  this.loadingService.show();
  this.adminService.getGobalWorklist(payload).pipe(
    map((response: VwCompleteWorklist[]) => {
      const gridResult: GridDataResult = {
        data: response || [],
        total: response[0]?.totalCount || 0,
      };
      this.globalGridView = gridResult;
      this.globalGridData = gridResult.data;
    }),
    catchError((error: Error) => {
      this.notificationService.show({
        content: error.message,
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'fade', duration: 1200 },
        type: { style: 'error', icon: true },
      });
      return of(null);
    }),
    finalize(() => this.loadingService.hide())
  ).subscribe();
}

  onGobalWaitListSelectionChange(event: SelectionEvent): void {
    if (event.selectedRows.length > 0) {
      event.selectedRows.forEach((row: RowArgs) =>
        this.selectedAgency.push(row.dataItem)
      );
    }
    this.selectedGlobalWaitlist = this.globalMySelection;
  }

  onSelectionChange(event: SelectionEvent): void {
    if (event.selectedRows.length > 0) {
      event.selectedRows.forEach((row: RowArgs) =>
        this.selectedWaitlist.push(row.dataItem)
      );
    }
  }

  public addWaitList(): void {
    this.loadingService.show();
    const payload: AgencyRequest[] = this.selectedWaitlist.map(
      (data: AgencyRequest) => ({
        // display the loader once button is clicked

        // construct payload
        agencyLocationSk: data?.agencyLocationSk,
        studentSk: data?.studentSk,
      })
    );

    this.adminService
      .addToWorkList(payload) // make the first API call to get workList details
      .pipe(
        catchError((error: Error) => {
          // if first API call fails, notification message is displayed to the UI
          return throwError(() => new Error(error.message));
        }),
        switchMap(() => {
          // first API call is successful, data has been submitted successfully
          this.notificationService.show({
            content: 'Submitted Successfully',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'success', icon: true },
          });

          // make the second API call
          this.mdeSkip = 0; // reset to first page
          return this.getMdeWaitlist();
        }),
        finalize(() => this.loadingService.hide()) //no matter if either call passes or fails, stop the loader
      )
      .subscribe({
        next: (gridResult: GridDataResult) => {
          // save the mdeWaitList (once second API call is successful) and reset the selection
          this.mySelection = [];
          this.saveMdeWaitlist(gridResult?.data);
        },
        error: (error: Error) => {
          this.notificationService.show({
            content: error.message,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'fade', duration: 1200 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  public removeWaitList(): void {
    //display the loader
    this.loadingService.show();

    // construct the payload
    const payload: AgencyRequest[] = this.selectedWaitlist.map(
      (data: AgencyRequest) => ({
        agencyLocationSk: data?.agencyLocationSk,
        studentSk: data?.studentSk,
      })
    );

    this.adminService
      .removeFromWaitlist(payload)
      .pipe(
        catchError((error: Error) => {
          // if first API call fails, notification message is displayed to the UI
          return throwError(() => new Error(error.message));
        }),
        switchMap(() => {
          // first API call is successful, data has been submitted successfully
          this.notificationService.show({
            content: 'Submitted Successfully',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'success', icon: true },
          });

          // make the second API call
          return this.getMdeWaitlist();
        }),
        finalize(() => this.loadingService.hide()) // if api calls fail or pass stop displaying the loader
      )
      .subscribe({
        next: (gridResult: GridDataResult) => {
          // save the mdeWaitList (once second API call is successful) and reset the selection
          this.mySelection = [];
          this.saveMdeWaitlist(gridResult?.data);
        },
        error: (error: Error) => {
          this.notificationService.show({
            content: error.message,
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  onPageChange(event?: PageChangeEvent): void {
    this.mdeSkip = event?.skip || 0;
    this.mdePageSize = event?.take || this.mdePageSize;
    this.getMdeWaitlist().subscribe();
  }  

  openNewReferralModalHandler(): void {
    this.showNewReferralModal$.next(true);
  }

  closeModalHandler(): void {
    this.showNewReferralModal$.next(false);
    this.mdeWaitListForm.get('mdeWaitListSearch').reset();
    this.onPageChange();
  }

  openShowAssignStudentToAgencies(): void {
    this.showAssignStudentToAgencies$.next(true);
  }

  closeAssignStudentToAgencieseModalHandler(): void {
    this.showAssignStudentToAgencies$.next(false);
    this.globalMySelection = [];
    this.globalForm.get('globalSearch').reset();
    this.onGlobalPageChange();
  }

  onGlobalPageChange(event?: PageChangeEvent): void {
    this.globalSkip = event?.skip || 0;
    this.globalPageSize = event?.take || this.globalPageSize;
    this.gobalWorklist();
  }


  get hasAssignAgencyPermission(): boolean {
    return some(
      this.user?.selectedRole?.userPermissions,
      (x: Permission) => x.permissionId === Permissions.CanAssignToAgency
    );
  }

  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    const digits: string = phone.replace(/\D/g, '');
    if (digits.length === this.phoneLength) {
      return `(${digits.slice(0, this.areaCode)}) ${digits.slice(
        this.areaCode,
        this.phoneNumberEnd
      )}-${digits.slice(this.phoneNumberEnd)}`;
    }
    return phone;
  }
}
